import { ErrorHandler, Inject, Injectable, InjectionToken, Injector, NgZone } from "@angular/core";
import { Firestore } from "@angular/fire/firestore";
import { AuthService } from "@core/auth.service";
import { IndexedDBRecoveryService } from "@core/indexeddb-recovery.service";
import { FirebaseError } from "firebase/app";
import * as Rollbar from "rollbar";
import { of, switchMap } from "rxjs";
import { environment } from 'src/environments/environment';
import { FirestoreDataService } from "./firestore-data.service";

export const RollbarService = new InjectionToken<Rollbar>("rollbar");

/**
 * [LOTTOSOCIAL-PWA-1N7S / LOTTOSOCIAL-PWA-1NCT]
 * Returns true when a thrown value looks like a minified Angular/Firebase internal error
 * triggered by a network drop on iPhone/WebKit (Chrome Mobile iOS). Two forms are matched:
 *   • Bare minified identifier:    "Ba", "fa", "La"  (error.message === 1–3 letters)
 *   • Re-throw-wrapped identifier: "Error: Ba", "Error: La"  (produced by the now-removed
 *     `throw new Error(\`Error: \${err}\`)` pattern; kept as defence-in-depth for cached bundles)
 *
 * [A-Za-z]{1,3} is letters-only to exclude digit-containing codes such as "404" or "500"
 * that \w{1,3} would also match. All-letter 1–3 char strings (including acronyms) are an
 * accepted tradeoff — minified identifiers are indistinguishable from such acronyms here.
 *
 * Typed as `unknown` because Angular's ErrorHandler can receive non-Error throwables.
 */
function isLikelyMinifiedWebKitNetworkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as { name?: unknown; message?: unknown };
  if (err.name !== 'Error' || typeof err.message !== 'string') return false;
  const msg = err.message;
  // Bare form:          "La"             (original short minified message)
  // Single-wrap form:   "Error: La"      (err was a string, template literal = "Error: La")
  // Double-wrap form:   "Error: Error: La" (err was Error("La"), .toString() = "Error: La",
  //                                        template literal = "Error: Error: La")
  // ^(?:Error: ){0,2} covers 0, 1, or 2 "Error: " prefixes.
  return /^(?:Error: ){0,2}[A-Za-z]{1,3}$/.test(msg);
}

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  translatedToastMsg: any;
  selectedLanguage: string = "en";
  private indexedDBRecovery: IndexedDBRecoveryService;

  constructor(
    private authService: AuthService,
    private firestore: Firestore,
    @Inject(RollbarService) private rollbar: Rollbar,
    private injector: Injector,
    private firestoreDataService: FirestoreDataService,
    private ngZone: NgZone
  ) {
    this.selectedLanguage = localStorage.getItem("language_code") ? localStorage.getItem("language_code") : "en";
    this.indexedDBRecovery = this.injector.get(IndexedDBRecoveryService);
  }

  handleError(error: Error) {
    if (!error) return;
    if (window.location.href.includes("localhost")) {
      return; // Skip error handling on localhost
    }

    // Check for IndexedDB corruption errors first
    if (this.indexedDBRecovery.isIndexedDBCorruptionError(error)) {
      this.indexedDBRecovery.handleCorruptionAndReload().catch(err => {
        return err;
      });
      return;
    }

    // Add Firebase auth error handling
    if (error instanceof FirebaseError) {
      if (["auth/unauthenticated", "auth/invalid-credential"].includes(error.code)) {
        // Attempt to recover from auth error
        this.handleAuthError();
        return;
      }
    }

    // Handle Firebase Installations IndexedDB error caused by privacy-restricting browsers
    // (e.g. DuckDuckGo Mobile on iOS) that block IndexedDB, causing IDBOpenDBRequest.result
    // to be null/undefined when the 'upgradeneeded' callback fires.
    if (this.indexedDBRecovery.isCreateObjectStoreRaceCondition(error)) {
      const authService = this.injector.get(AuthService);
      authService.errorHandlerLogs('[Firebase] IndexedDB unavailable (private/restricted browser mode):', error);
      return;
    }

    // Handle external CRM SDK errors (connect.js from sdk.crmback.io)
    // The SDK has a known bug where it fails to check if response.data exists before accessing response.data.error
    if (this.isCRMSDKError(error)) {
      // Log as warning instead of error since this is a third-party SDK issue
      this.handleWarrningRollBar(error);
      return;
    }

    // [LOTTOSOCIAL-PWA-1N9Q] Handle LiveChat widget timeout errors that conflict with Firebase Firestore
    // The LiveChat widget creates a timeout Error and sets .code="TIMEOUT" on it. Zone.js (Angular
    // polyfills) intercepts the async task and makes the Error non-extensible. Firebase Firestore's
    // internal error handler then tries to assign .code to this sealed object, throwing a TypeError.
    // This is a third-party conflict with no impact on app functionality — downgrade to a warning.
    if (this.isLiveChatFirestoreConflictError(error)) {
      this.handleWarrningRollBar(error);
      return;
    }

    // Handle external script errors trying to access 'dataset' on undefined elements
    // This occurs when third-party scripts (GTM, marketing campaigns, widgets) attempt to
    // manipulate DOM elements that don't exist in the PWA structure
    if (this.isExternalDatasetError(error)) {
      console.warn("External script dataset access error (non-critical):", error.message);
      // Log as warning since this is a third-party script issue, not our code
      this.handleWarrningRollBar(error);
      return;
    }

    const reference = /ReferenceError: Can't find variable: Notification/;
    if (reference.test(error.message)) {
      this.handleWarrningRollBar(error);
      return;
    }

    // [LOTTOSOCIAL-PWA-1N7S / LOTTOSOCIAL-PWA-1NCT] Suppress minified Angular/Firebase
    // internal errors that surface on iPhone/WebKit when the network drops mid-session.
    // Two forms are matched by isLikelyMinifiedWebKitNetworkError():
    //   • bare identifier:    "Ba", "fa", "La"  (original short minified message)
    //   • wrapped identifier: "Error: Ba", "Error: La"  (re-thrown with "Error: " prefix)
    // See that helper for the rationale behind [A-Za-z]{1,3}.
    if (isLikelyMinifiedWebKitNetworkError(error)) {
      this.authService.errorHandlerLogs('[GlobalErrorHandler] Minified internal error suppressed (likely network-triggered on WebKit):', error);
      this.handleWarrningRollBar(error);
      return;
    }

    // [LOTTOSOCIAL-PWA-1N6X] Handle stale PWA bundle serving old voucher throwError path.
    // The current vouchers.service.ts returns EMPTY when userId/voucherId is missing, but
    // users with a cached older bundle may still hit the previous throwError path.
    // Treat as a warning and force a cache-clear reload so the user gets the fresh bundle.
    if (error?.message?.includes('No userId or voucherId found')) {
      console.warn('[GlobalErrorHandler] Stale bundle detected (No userId or voucherId found) — clearing cache and reloading.', error);
      this.handleWarrningRollBar(error);
      if ('caches' in self) {
        caches.keys().then((keyList) => {
          return Promise.all(keyList.map((key) => caches.delete(key)));
        }).catch(() => {}).finally(() => {
          window.location.reload();
        });
      } else {
        window.location.reload();
      }
      return;
    }

    let errorData: any;
    if (Object.keys(error).length !== 0 && error.name && error.message) {
      errorData = {
        name: error.name || "Error",
        message: error.message.toString() || error.toString(),
        createdOn: Date.now(),
      };
    } else {
      errorData = {
        name: "TypeError",
        message: error?.message?.toString() || error.toString(),
        createdOn: Date.now(),
      };
    }
    if (!errorData) return;

    if (errorData.message.includes("Loading chunk")) {
      const authService = this.injector.get(AuthService);
      const path_id = "G2hzy28FznIF29mIsjoR";
      const filter = ["static_messages_pwa"];
      const method = "read";

      authService.getV2CloudFunction("updatefirestoredoc", {
        path_id,
        filter,
        method,
      }).subscribe({
        next: (response: any) => {
          this.translatedToastMsg = response?.data?.static_messages_pwa;

          const msg = this.translatedToastMsg?.["1096"]?.[this.selectedLanguage];

          authService
            .getConstantsMsg("1096")
            .then((translatedMsg) => {
              const defaultMsg = {
                message: {
                  line_1: "Some technical glitch occur, be with us, Reloading...",
                  line_2: "",
                },
                style: {
                  icon: "tick circle alert.svg",
                  type: "warning",
                },
              };
              authService.showToastMessage(translatedMsg, defaultMsg);
            })
            .catch((error) => {
              return error;
            });
        },
        error: () => {
          return false;
        },
      });

      const cachesToKeep = ["v2"];
      caches.keys().then((keyList) => {
        return Promise.all(
          keyList.map((key) => {
            if (!cachesToKeep.includes(key)) {
              return caches.delete(key);
            }
          })
        );
      }).catch((error) => {
        return error;
      });

      window.location.reload();
      return;
    }

    if (navigator.onLine) {
      const path_id = "6JPlsfdJhWLVMNh0bAKG";
      const doc_data = errorData;
      const method = "update";

      this.firestoreDataService.getV2CloudFunction("updatefirestoredoc", {
        path_id,
        doc_data,
        method,
      }).subscribe({
        error: (err) => {
          throw err;
        }
      });
      this.handleErrorRollBar(error);
    }
  }
  private handleWarrningRollBar(err: any): void {
    if (window.location.href.search("localhost") > -1) {
      return;
    }
    this.rollbar.warning(err.originalError || err);
  }

  private handleErrorRollBar(err: any): void {
    const authService = this.injector.get(AuthService);
    if (window.location.href.search("localhost") > -1) {
      return;
    }
    authService.getUid$("GlobalErrorHandler").pipe(
      switchMap((userId) => {
        if (!userId) userId = null;
        this.rollbar.configure({
          payload: {
            person: {
              id: userId ? userId : "loggedOutState",
              username: userId,
              sw: localStorage.getItem("sw"),
              swUpdatedAt: localStorage.getItem("swUpdatedAt"),
              email: localStorage.getItem("email"),
              environment: "Live",
            },
          },
        });
        return of(userId) || of(null);
      }));


    this.rollbar.error(err.originalError || err);
  }
  // method to handle authentication errors
  private handleAuthError() {
    const authService = this.injector.get(AuthService);
    return authService.getUid$("GlobalErrorHandler").pipe(
      switchMap((userId) => {
        if (!userId) userId = null;
        try {
          if (userId) {
            // Try to refresh the token
            authService.getCustomToken(userId).subscribe(
              (res) => {
                if (res?.customToken) {
                  localStorage.setItem("fbToken", res.customToken);
                }
              },
              (err) => console.error("Failed to refresh token:", err)
            );
          }
        } catch (error) {
          return error;
        }
        return of(userId) || of(null);
      }));

  }

  /**
   * Detects if an error originates from the external CRM SDK (connect.js)
   * The CRM SDK has a known bug where it doesn't check if response.data exists
   * before accessing response.data.error, causing TypeErrors during failed API requests
   */
  private isCRMSDKError(error: Error): boolean {
    if (!error || !error.message) return false;

    const errorMessage = error.message.toLowerCase();
    const errorStack = (error.stack ?? '').toLowerCase();

    // Check for the specific error pattern from the CRM SDK
    const isCRMErrorPattern =
      errorMessage.includes("cannot read propert") &&
      errorMessage.includes("'error'") &&
      errorMessage.includes("undefined");

    // Check if the error originates from connect.js (CRM SDK)
    const isFromCRMSDK =
      errorStack.includes('connect.js') ||
      errorStack.includes('sdk.crmback.io') ||
      errorStack.includes('crmback');

    // Return true only if both conditions are met
    return isCRMErrorPattern && isFromCRMSDK;
  }

  /**
   * Detects the conflict between the LiveChat widget timeout and Firebase Firestore's error handler.
   * The LiveChat widget creates a timeout Error that becomes non-extensible via Zone.js, and
   * Firebase Firestore's internal `ht` error constructor then fails trying to set `.code` on it.
   * Requires: a non-extensible `.code` assignment TypeError, a LiveChat widget stack frame
   * (`widget/v2/plugin` or `@@livechat`), and a Firestore/Firebase stack frame.
   */
  private isLiveChatFirestoreConflictError(error: Error): boolean {
    if (!error || !error.message) return false;
    if (!(error instanceof TypeError) && error.name !== 'TypeError') return false;

    const message = error.message.toLowerCase();
    const isNonExtensibleCodeError =
      message.includes('code') &&
      (message.includes('object is not extensible') ||
        message.includes('cannot define property') ||
        message.includes("can't define property"));
    if (!isNonExtensibleCodeError) return false;

    const errorStack = (error.stack ?? '').toLowerCase();
    const isFromLiveChatWidget =
      errorStack.includes('widget/v2/plugin') ||
      errorStack.includes('@@livechat');
    const isFromFirestore =
      errorStack.includes('firestore') ||
      errorStack.includes('firebase');

    return isFromLiveChatWidget && isFromFirestore;
  }

  /**
   * Detects if an error originates from external scripts trying to access 'dataset' property
   * on undefined elements. This commonly occurs when third-party scripts (GTM, marketing campaigns,
   * external widgets) attempt to manipulate DOM elements that don't exist in the PWA structure.
   */
  private isExternalDatasetError(error: Error): boolean {
    if (!error || !error.message) return false;

    const errorMessage = error.message.toLowerCase();
    const errorStack = (error.stack ?? '').toLowerCase();

    // Check for the specific error pattern: accessing 'dataset' on undefined
    const isDatasetErrorPattern = 
      errorMessage.includes("cannot read propert") && 
      errorMessage.includes("'dataset'") &&
      errorMessage.includes("undefined");

    // Check if the error is from minified code (external scripts are typically minified)
    // and NOT from our application code (our code would have clearer stack traces)
    const isFromExternalScript = 
      errorStack.includes('main.') && errorStack.includes('.js') && 
      !errorStack.includes('src/') && 
      !errorStack.includes('app/');

    // Additional check: errors related to grid/card structures that don't exist in our PWA
    const hasExternalGridPattern = 
      errorStack.includes('zoom') || 
      errorStack.includes('grid') ||
      errorStack.includes('cards[');

    // Return true if this looks like an external script error
    return isDatasetErrorPattern && (isFromExternalScript || hasExternalGridPattern);
  }
}

export function rollbarFactory() {
  const authService = this.injector.get(AuthService);
  return authService.getUid$("RollbarFactory").pipe(
    switchMap(async (userId) => {
      if (!userId) userId = null;
      const rollbarConfig = {
        accessToken: environment.rollbarAccessToken,
        captureUncaught: true,
        captureUnhandledRejections: true,
        nodeSourceMaps: false,
        inspectAnonymousErrors: true,
        ignoreDuplicateErrors: true,
        wrapGlobalEventHandlers: false,
        scrubRequestBody: true,
        exitOnUncaughtException: false,
        stackTraceLimit: 20,
        environment: "Live",
        payload: {
          environment: "Live",
          client: {
            javascript: {
              source_map_enabled: true,
              code_version: new Date().toISOString().replace('-', '.').split('T')[0].replace('-', '.'),
              guess_uncaught_frames: true,
              person: {
                id: userId,
                username: localStorage.getItem("userNick"),
                email: localStorage.getItem("email"),
                sw: localStorage.getItem("sw"),
                swUpdatedAt: localStorage.getItem("swUpdatedAt"),
                environment: "Live",
                currentURL: window.location.href,
              },
            },
          },
        },
      };
      return new Rollbar(rollbarConfig);
    }));
}
